// ============================================================================
// USER ADMIN SERVICE
// ============================================================================
// API calls for admin user management

import { get, post, put, del } from '../client/axiosClient';
import { userAdminEndpoints } from '../endpoints/iam.endpoints';
import { buildEndpointWithQuery } from '../endpoints/base.endpoints';
import type {
  ApiResponse,
  BackendPagedResult,
  PaginatedResponse,
  EmptyResponse,
  ID,
} from '../types';
import type {
  UserAdminDto,
  CreateUserAdminRequest,
  UpdateUserAdminRequest,
  UserAdminQueryParams,
  UserLookupResult,
} from '../types/iam.types';

class UserAdminService {
  async getAll(params?: UserAdminQueryParams): Promise<PaginatedResponse<UserAdminDto>> {
    const endpoint = params
      ? buildEndpointWithQuery(userAdminEndpoints.list(), params)
      : userAdminEndpoints.list();
    const response = await get<ApiResponse<BackendPagedResult<UserAdminDto>>>(endpoint);
    const paged = response.data.data;
    return {
      success: response.data.success,
      message: response.data.message,
      data: Array.from(paged.items || []),
      pagination: {
        currentPage: paged.page,
        pageSize: paged.pageSize,
        totalPages: paged.totalPages,
        totalItems: paged.totalCount,
        hasNextPage: paged.hasNext,
        hasPreviousPage: paged.hasPrevious,
      },
    };
  }

  async getById(id: ID): Promise<ApiResponse<UserAdminDto>> {
    const response = await get<ApiResponse<UserAdminDto>>(userAdminEndpoints.getById(id));
    return response.data;
  }

  async create(data: CreateUserAdminRequest): Promise<ApiResponse<UserAdminDto>> {
    const response = await post<ApiResponse<UserAdminDto>>(userAdminEndpoints.create(), data);
    return response.data;
  }

  async update(id: ID, data: UpdateUserAdminRequest): Promise<ApiResponse<UserAdminDto>> {
    const response = await put<ApiResponse<UserAdminDto>>(userAdminEndpoints.update(id), data);
    return response.data;
  }

  async delete(id: ID): Promise<EmptyResponse> {
    const response = await del<EmptyResponse>(userAdminEndpoints.delete(id));
    return response.data;
  }

  async isUserNameAvailable(userName: string, excludeId?: ID): Promise<boolean> {
    const response = await get<ApiResponse<boolean>>(
      userAdminEndpoints.validateUserName(userName, excludeId)
    );
    return response.data.data;
  }

  async lookupUser(userName: string): Promise<UserLookupResult> {
    const response = await get<ApiResponse<UserLookupResult>>(
      userAdminEndpoints.lookup(userName)
    );
    return response.data.data;
  }
}

export const userAdminService = new UserAdminService();
export default userAdminService;
